// src/components/HomePageEditModal.js
// Updated: 11/17/2025 02:32 PM

import React, { useState, useEffect } from 'react';
import {
  Modal, Box, Typography, TextField, Button, CircularProgress, IconButton,
  List, ListItem, ListItemText, Paper,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../context/AuthContext';
import { uploadFile, deleteFileByUrl } from '../services/storageService';

import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListItemNode, ListNode } from '@lexical/list';
import { CodeHighlightNode, CodeNode } from '@lexical/code';
import { AutoLinkNode, LinkNode } from '@lexical/link';

const resizeImage = (file, maxWidth, maxHeight, quality = 0.7) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width *= maxHeight / height;
            height = maxHeight;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob((blob) => {
          if (blob) {
            resolve(new File([blob], file.name, { type: 'image/jpeg', lastModified: Date.now() }));
          } else {
            reject(new Error('Canvas toBlob failed'));
          }
        }, 'image/jpeg', quality);
      };
      img.onerror = (error) => reject(error);
    };
  });
};

const editorTheme = {
  ltr: 'ltr',
  rtl: 'rtl',
  placeholder: 'editor-placeholder',
  paragraph: 'editor-paragraph',
};

const editorConfig = {
  namespace: 'HomePageEditor',
  theme: editorTheme,
  onError(error) {
    throw error;
  },
  nodes: [
    HeadingNode, QuoteNode, ListItemNode, ListNode,
    CodeHighlightNode, CodeNode, AutoLinkNode, LinkNode,
  ],
};

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: 700 },
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
  maxHeight: '90vh',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column'
};

function HomePageEditModal({ open, onClose, homePageData }) {
  const { userProfile } = useAuth();
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [pastorMessage, setPastorMessage] = useState('');
  const [verseOfTheWeek, setVerseOfTheWeek] = useState('');
  const [heroImages, setHeroImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const getInitialEditorState = (content) => {
    try {
      if (typeof content === 'string' && content.trim().startsWith('{')) {
        JSON.parse(content);
        return content;
      }
    } catch (e) {
      // Not valid JSON
    }
    return '{"root":{"children":[{"children":[],"direction":null,"format":"","indent":0,"type":"paragraph","version":1}],"direction":null,"format":"","indent":0,"type":"root","version":1}}';
  };

  useEffect(() => {
    if (homePageData) {
      setWelcomeMessage(getInitialEditorState(homePageData.welcomeMessage));
      setPastorMessage(homePageData.pastorMessage || '');
      setVerseOfTheWeek(getInitialEditorState(homePageData.verseOfTheWeek));
      setHeroImages(homePageData.heroImages || []);
    } else {
       setWelcomeMessage(getInitialEditorState(''));
       setVerseOfTheWeek(getInitialEditorState(''));
       setHeroImages([]);
    }
  }, [homePageData, open]);

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0 || !userProfile?.churchId) return;

    setUploading(true);
    try {
      const uploadPromises = files.map(async (file) => {
        const resizedFile = await resizeImage(file, 1920, 1080, 0.75);
        const imagePath = `churches/${userProfile.churchId}/homepage/${Date.now()}_${resizedFile.name}`;
        const imageUrl = await uploadFile(resizedFile, imagePath);
        return { url: imageUrl, name: resizedFile.name };
      });

      const newImages = await Promise.all(uploadPromises);
      setHeroImages(prevImages => [...prevImages, ...newImages]);

    } catch (error) {
      console.error("Error uploading hero images:", error);
      alert("Failed to upload one or more images.");
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = async (imageToRemove) => {
    if (!window.confirm("Are you sure you want to remove this image?")) return;
    try {
      await deleteFileByUrl(imageToRemove.url);
      setHeroImages(prevImages => prevImages.filter(img => img.url !== imageToRemove.url));
    } catch (error) {
      console.error("Error deleting hero image:", error);
      alert("Failed to delete image.");
    }
  };

  const handleSave = async () => {
    if (!userProfile?.churchId) return;
    setSaving(true);
    const docRef = doc(db, 'churches', userProfile.churchId, 'siteContent', 'homePage');
    try {
      await setDoc(docRef, {
        welcomeMessage,
        pastorMessage,
        verseOfTheWeek,
        heroImages,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      alert("Home page content updated successfully!");
      onClose();
    } catch (error) {
      console.error("Error saving home page content:", error);
      alert("Failed to save content: " + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleWelcomeMessageChange = (editorState) => {
    setWelcomeMessage(JSON.stringify(editorState.toJSON()));
  };

  const handleVerseOfTheWeekChange = (editorState) => {
    setVerseOfTheWeek(JSON.stringify(editorState.toJSON()));
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={style}>
        <Typography variant="h6" component="h2">Edit Home Page Content</Typography>

        <Box sx={{ my: 2 }}>
          <Typography variant="subtitle1" gutterBottom>Welcome Message</Typography>
          <LexicalComposer initialConfig={{...editorConfig, editorState: welcomeMessage}}>
            <Paper variant="outlined">
              <RichTextPlugin
                contentEditable={<ContentEditable style={{ minHeight: 150, padding: '10px' }} />}
                placeholder={<div style={{ position: 'absolute', top: 30, left: 20, color: '#aaa' }}>Enter your welcome message...</div>}
                ErrorBoundary={LexicalErrorBoundary}
              />
            </Paper>
            <HistoryPlugin />
            <OnChangePlugin onChange={handleWelcomeMessageChange} />
          </LexicalComposer>
        </Box>

        <Box sx={{ my: 2 }}>
          <Typography variant="subtitle1" gutterBottom>Verse of the Week</Typography>
          <LexicalComposer initialConfig={{...editorConfig, editorState: verseOfTheWeek}}>
            <Paper variant="outlined">
              <RichTextPlugin
                contentEditable={<ContentEditable style={{ minHeight: 100, padding: '10px' }} />}
                placeholder={<div style={{ position: 'absolute', top: 30, left: 20, color: '#aaa' }}>Enter the verse of the week...</div>}
                ErrorBoundary={LexicalErrorBoundary}
              />
            </Paper>
            <HistoryPlugin />
            <OnChangePlugin onChange={handleVerseOfTheWeekChange} />
          </LexicalComposer>
        </Box>

        <TextField
          margin="normal" fullWidth label="Pastor's Corner / Weekly Word" multiline rows={4}
          value={pastorMessage} onChange={(e) => setPastorMessage(e.target.value)}
        />

        <Box sx={{ mt: 2, border: '1px dashed grey', p: 2 }}>
          <Typography variant="subtitle1" gutterBottom>Hero Images (Carousel)</Typography>
          <List dense>
            {heroImages.map((image, index) => (
              <ListItem
                key={index}
                sx={{ alignItems: 'center' }} // Vertically center items
                secondaryAction={
                  <IconButton edge="end" aria-label="delete" onClick={() => handleRemoveImage(image)}>
                    <DeleteIcon />
                  </IconButton>
                }
              >
                {/* --- START OF FIX --- */}
                {/* This Box replaces ListItemAvatar to show a proper preview */}
                <Box
                  sx={{
                    width: 100, // Wider preview
                    height: 56,  // Consistent height
                    mr: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'grey.100', // Background for letterboxing
                    borderRadius: 1,
                    overflow: 'hidden',
                  }}
                >
                  <img
                    src={image.url}
                    alt={image.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain' // Shows the full image, not cropped
                    }}
                  />
                </Box>
                {/* --- END OF FIX --- */}
                <ListItemText
                  primary={image.name}
                  primaryTypographyProps={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                />
              </ListItem>
            ))}
          </List>
          <Button
            variant="outlined"
            component="label"
            startIcon={<CloudUploadIcon />}
            disabled={uploading}
          >
            Add Image(s)
            <input type="file" hidden accept="image/*" onChange={handleImageUpload} multiple />
          </Button>
          {uploading && <CircularProgress size={24} sx={{ ml: 2 }} />}
        </Box>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={24} /> : 'Save Changes'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}

export default HomePageEditModal;